import { useCallback, useEffect, useState } from 'react';
import { fetchVault } from '@/lib/librarian/api';
import { initializeVault } from '@/lib/librarian/initializeVault';
import type { KnowledgeBaseVault } from '@/lib/librarian/types';
import { toast } from '@/components/ui/use-toast';

export function useKnowledgeBaseVault(knowledgeBaseId?: string | null) {
  const [vault, setVault] = useState<KnowledgeBaseVault | null>(null);
  const [loading, setLoading] = useState(false);

  const refreshVault = useCallback(async () => {
    if (!knowledgeBaseId) {
      setVault(null);
      return null;
    }

    setLoading(true);
    try {
      const nextVault = await fetchVault(knowledgeBaseId);
      setVault(nextVault);
      return nextVault;
    } catch (error) {
      console.error('Failed to fetch knowledge base vault:', error);
      toast({ title: 'Error', description: 'Failed to load the Librarian vault.', variant: 'destructive' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [knowledgeBaseId]);

  const ensureVault = useCallback(async () => {
    if (!knowledgeBaseId) return null;

    setLoading(true);
    try {
      const nextVault = await initializeVault(knowledgeBaseId);
      setVault(nextVault);
      return nextVault;
    } catch (error) {
      console.error('Failed to initialize knowledge base vault:', error);
      toast({ title: 'Error', description: 'Failed to initialize the Librarian vault.', variant: 'destructive' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [knowledgeBaseId]);

  useEffect(() => {
    void refreshVault();
  }, [refreshVault]);

  return {
    vault,
    loading,
    refreshVault,
    ensureVault,
  };
}
